import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { detectarDuplicidade, idCodigo, type Duplicidade, type TipoCodigo } from '@profitlens/dominio';
import { db } from '../config/firebase';
import type { CodigoProduto, Produto } from '../tipos';

export type RascunhoProduto = Omit<Produto, 'id' | 'criadoEm' | 'criadoPor' | 'atualizadoEm' | 'atualizadoPor'>;

export function produtoVazio(): RascunhoProduto {
  return {
    nome: '',
    descricao: null,
    marca: null,
    modelo: null,
    categoriaId: null,
    codigoInterno: null,
    unidadeMedida: 'UN',
    qtdPorEmbalagemMilli: 1_000, // 1 unidade
    pesoGramas: null,
    comprimentoMm: null,
    larguraMm: null,
    alturaMm: null,
    ncm: null,
    palavrasChave: [],
    sinonimos: [],
    precoAlvoCompraCents: null,
    precoMinimoVendaCents: null,
    observacoes: null,
    ativo: true,
  };
}

export function useProdutos() {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'produtos'), orderBy('nome')),
        (s) => {
          setProdutos(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Produto));
          setCarregando(false);
        },
        (e) => {
          setErro(e.message);
          setCarregando(false);
        },
      ),
    [],
  );

  return { produtos, carregando, erro };
}

export function useCodigos(produtoId: string | null) {
  const [codigos, setCodigos] = useState<CodigoProduto[]>([]);

  useEffect(() => {
    if (!produtoId) {
      setCodigos([]);
      return;
    }
    return onSnapshot(query(collection(db, 'codigosProduto'), where('produtoId', '==', produtoId)), (s) =>
      setCodigos(s.docs.map((d) => ({ id: d.id, ...d.data() }) as CodigoProduto)),
    );
  }, [produtoId]);

  return codigos;
}

/** Índice de código por produto, para a listagem e a checagem de duplicidade. */
export function useIndiceCodigos() {
  const [porProduto, setPorProduto] = useState<Record<string, string[]>>({});

  useEffect(
    () =>
      onSnapshot(collection(db, 'codigosProduto'), (s) => {
        const mapa: Record<string, string[]> = {};
        for (const d of s.docs) {
          const c = d.data() as CodigoProduto;
          (mapa[c.produtoId] ??= []).push(c.valor);
        }
        setPorProduto(mapa);
      }),
    [],
  );

  return porProduto;
}

export function useDuplicidades(
  rascunho: { nome: string; marca: string | null },
  codigos: string[],
  ignorarId: string | null,
): Duplicidade[] {
  const { produtos } = useProdutos();
  const indice = useIndiceCodigos();

  return useMemo(() => {
    if (rascunho.nome.trim().length < 3) return [];
    const candidatos = produtos
      .filter((p) => p.id !== ignorarId)
      .map((p) => ({ id: p.id, nome: p.nome, marca: p.marca, codigos: indice[p.id] ?? [] }));
    return detectarDuplicidade({ ...rascunho, codigos }, candidatos);
  }, [produtos, indice, rascunho.nome, rascunho.marca, codigos.join('|'), ignorarId]);
}

export async function salvarProduto(
  id: string | null,
  dados: RascunhoProduto,
  usuarioId: string,
): Promise<string> {
  const ref = id ? doc(db, 'produtos', id) : doc(collection(db, 'produtos'));
  if (id) {
    await updateDoc(ref, { ...dados, atualizadoEm: serverTimestamp(), atualizadoPor: usuarioId });
  } else {
    await setDoc(ref, {
      ...dados,
      criadoEm: serverTimestamp(),
      criadoPor: usuarioId,
      atualizadoEm: null,
      atualizadoPor: null,
    });
  }
  return ref.id;
}

/**
 * Adiciona um código.
 *
 * A unicidade global (RN-020) vem do ID do documento: `{tipo}_{valor}`. Dois
 * documentos com o mesmo ID não coexistem, e a transação garante que a leitura
 * e a escrita aconteçam sem brecha entre elas — dois cadastros simultâneos do
 * mesmo GTIN não passam os dois.
 */
export async function adicionarCodigo(
  produtoId: string,
  tipo: TipoCodigo,
  valor: string,
  principal: boolean,
): Promise<void> {
  const id = idCodigo(tipo, valor);
  await runTransaction(db, async (tx) => {
    const ref = doc(db, 'codigosProduto', id);
    const atual = await tx.get(ref);
    if (atual.exists()) {
      const dono = atual.data() as CodigoProduto;
      throw new Error(
        dono.produtoId === produtoId
          ? 'Este código já está neste produto.'
          : 'Este código já pertence a outro produto.',
      );
    }
    tx.set(ref, { produtoId, tipo, valor, principal });
  });

  if (principal) await tornarPrincipal(produtoId, id);
}

/** No máximo um código principal por produto (RN-021). */
export async function tornarPrincipal(produtoId: string, codigoId: string): Promise<void> {
  const s = await getDocs(query(collection(db, 'codigosProduto'), where('produtoId', '==', produtoId)));
  await Promise.all(
    s.docs.map((d) =>
      d.data().principal === (d.id === codigoId)
        ? Promise.resolve()
        : updateDoc(d.ref, { principal: d.id === codigoId }),
    ),
  );
}

export async function removerCodigo(codigoId: string): Promise<void> {
  await deleteDoc(doc(db, 'codigosProduto', codigoId));
}
